'use client';

/**
 * TimeRangePicker — Phase F2 Sprint 1.
 *
 * Standard dashboard time-range selector used at the top of every
 * analytics surface. Closes the universal gap from the Phase C audit:
 * 0/6 dashboards had a user-selectable time range.
 *
 * Two interaction modes (combined in the same widget):
 *   1. Preset chips — Today / 7d / 30d / 90d / 12m / Custom
 *   2. Custom mode opens two date inputs (from + to)
 *
 * Emits a normalised `TimeRange` value: `{ preset, from?, to? }`. Consumers
 * resolve `preset → ISO date range` server-side (the typical aged-care
 * dashboard already has tenant-tz-aware helpers; we don't replicate that
 * logic here).
 *
 * Consumer flow:
 *   const [range, setRange] = useState<TimeRange>({ preset: '30d' });
 *   <TimeRangePicker value={range} onChange={setRange} />
 *   // Server-side: resolve range.preset (or from/to) to the date filter.
 */

import React from 'react';
import { Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import { DateEntryInput } from '../date-entry-input';

export type TimeRangePreset = 'today' | '7d' | '30d' | '90d' | '12m' | 'custom';

export interface TimeRange {
  preset: TimeRangePreset;
  /** ISO date string (yyyy-mm-dd). Only meaningful when preset === 'custom'. */
  from?: string;
  /** ISO date string (yyyy-mm-dd). Only meaningful when preset === 'custom'. */
  to?: string;
}

export interface TimeRangePickerProps {
  value: TimeRange;
  onChange: (next: TimeRange) => void;
  /** Hide some presets per surface. Default = show all. */
  presets?: ReadonlyArray<TimeRangePreset>;
  className?: string;
}

const PRESET_LABELS: Record<TimeRangePreset, string> = {
  today: 'Today',
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  '12m': '12 months',
  custom: 'Custom',
};

const DEFAULT_PRESETS: ReadonlyArray<TimeRangePreset> = [
  'today',
  '7d',
  '30d',
  '90d',
  '12m',
  'custom',
];

export function TimeRangePicker({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  className,
}: TimeRangePickerProps) {
  // Pass-1 P2: derive custom-mode visibility directly from the controlled
  // value rather than maintaining a parallel useState. If a parent flips
  // `value.preset` to 'custom' programmatically (e.g. applying a saved
  // view that uses custom dates), the inputs reveal automatically.
  const customOpen = value.preset === 'custom';

  function handlePreset(preset: TimeRangePreset) {
    if (preset === 'custom') {
      onChange({ preset: 'custom', from: value.from, to: value.to });
      return;
    }
    onChange({ preset });
  }

  return (
    <div
      className={cn('inline-flex flex-wrap items-center gap-1.5', className)}
      role="group"
      aria-label="Time range"
      data-time-range-picker
    >
      <div
        role="radiogroup"
        aria-label="Time range preset"
        className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11.5px]"
      >
        {presets.map((preset) => {
          const active = value.preset === preset;
          return (
            <button
              key={preset}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => handlePreset(preset)}
              className={cn(
                'rounded px-2 py-0.5 font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
                active
                  ? 'bg-white text-statura-ink shadow-sm'
                  : 'text-text-muted hover:text-statura-ink',
              )}
            >
              {PRESET_LABELS[preset]}
            </button>
          );
        })}
      </div>

      {customOpen && (
        <div className="inline-flex items-center gap-1 text-[11.5px]">
          <Calendar className="h-3 w-3 text-text-muted" aria-hidden="true" />
          <DateEntryInput
            value={value.from ?? ''}
            onChange={(nextValue) =>
              onChange({ preset: 'custom', from: nextValue || undefined, to: value.to })
            }
            aria-label="From date"
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-statura-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible/40"
          />
          <span className="text-text-muted">→</span>
          <DateEntryInput
            value={value.to ?? ''}
            onChange={(nextValue) =>
              onChange({ preset: 'custom', from: value.from, to: nextValue || undefined })
            }
            aria-label="To date"
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-statura-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible/40"
          />
        </div>
      )}
    </div>
  );
}

/**
 * Resolve a `TimeRange` to a concrete `{ fromIso, toIso }` pair for
 * server queries. Tenant timezone is NOT applied here — callers should
 * thread the resolved range through `getDayInTz` if they need
 * tz-specific boundaries (consistent with the rest of the platform's
 * dates-in-tz discipline).
 *
 * Today           → midnight UTC of today → next midnight
 * 7d / 30d / 90d  → N days back from now → now
 * 12m             → 12 calendar months back from today → now
 * custom          → echo from / to (or undefined if unset)
 */
export function resolveTimeRangeToIso(range: TimeRange): { fromIso?: string; toIso?: string } {
  const now = new Date();
  switch (range.preset) {
    case 'today': {
      const start = new Date(now);
      start.setUTCHours(0, 0, 0, 0);
      return { fromIso: start.toISOString(), toIso: now.toISOString() };
    }
    case '7d':
    case '30d':
    case '90d': {
      const days = range.preset === '7d' ? 7 : range.preset === '30d' ? 30 : 90;
      const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      return { fromIso: start.toISOString(), toIso: now.toISOString() };
    }
    case '12m': {
      // 12 months back from "now" — compute via UTC-anchored ms arithmetic
      // (365 days) rather than .setMonth(). The Statura lint rule
      // statura/no-browser-local-date-methods bans .getMonth/.setMonth
      // because they use device-local time on mobile + Vercel-UTC on SSR,
      // which would make the resolved range silently drift across calls.
      // Consumers that need calendar-month-exact bounds in tenant tz
      // should apply getMonthInTz from @statura/utils/timezone after
      // calling this resolver.
      const start = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      return { fromIso: start.toISOString(), toIso: now.toISOString() };
    }
    case 'custom': {
      return {
        fromIso: range.from ? `${range.from}T00:00:00.000Z` : undefined,
        toIso: range.to ? `${range.to}T23:59:59.999Z` : undefined,
      };
    }
    default:
      return {};
  }
}
